/* The language, and the hook everything else uses.
 *
 * Context rather than a module-level variable, so switching re-renders. A
 * global would have been fewer lines and would have left the whole application
 * showing the old language until something else happened to change.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import { LANGS, initialLang, storeLang, translate, type Lang } from './i18n'

interface Ctx {
  lang: Lang
  setLang: (l: Lang) => void
  t: (key: string) => string
}

const LangCtx = createContext<Ctx>({
  lang: 'nb',
  setLang: () => {},
  t: (k) => k,
})

export function LangProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(initialLang)

  const setLang = useCallback((l: Lang) => {
    setLangState(l)
    storeLang(l)
    // The document says which language it is in, so a screen reader chooses the
    // right voice and a browser offers the right translation prompt. Easy to
    // forget, and invisible until somebody depends on it.
    document.documentElement.lang = l === 'nb' ? 'no' : 'en'
  }, [])

  const value = useMemo<Ctx>(
    () => ({ lang, setLang, t: (key: string) => translate(lang, key) }),
    [lang, setLang],
  )

  return <LangCtx.Provider value={value}>{children}</LangCtx.Provider>
}

/** `const t = useT()` then `t('Listen')`. */
export function useT(): (key: string) => string {
  return useContext(LangCtx).t
}

export function useLang(): Ctx {
  return useContext(LangCtx)
}

/** Two words, not a dropdown.
 *
 *  With exactly two languages a select is a control that hides one of them
 *  behind a click. Both are visible, the current one is marked, and switching
 *  is one tap -- which on a phone on a quay is the difference between using it
 *  and not.
 */
export function LangSwitch() {
  const { lang, setLang } = useLang()
  return (
    <div className="lang" role="group" aria-label="Language / Språk">
      {LANGS.map((l) => (
        <button
          key={l.code}
          type="button"
          aria-pressed={l.code === lang}
          onClick={() => setLang(l.code)}
        >
          {l.label}
        </button>
      ))}
    </div>
  )
}
